import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from './api';
import Spinner from './Spinner';
import CustomAlert from './CustomAlert';
import HamburgerMenu from './HamburgerMenu';
import ManningOrgChart from './ManningOrgChart';
import ManningLeavePanel from './ManningLeavePanel';
import ManningDailyAvailability from './ManningDailyAvailability';
import ManningReportsPanel from './ManningReportsPanel';
import ManningFunctionalOrg from './ManningFunctionalOrg';

const TABS = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'daily', label: 'Daily Availability' },
    { id: 'org-chart', label: 'Org Chart' },
    { id: 'functional-org', label: 'Functional Org' },
    { id: 'leave', label: 'Leave Chits' },
    { id: 'reports', label: 'Reports' },
    { id: 'employees', label: 'Employees' },
    { id: 'work-centers', label: 'Work Centers' },
    { id: 'gov-work-centers', label: 'Gov Work Center' },
    { id: 'shifts', label: 'Shifts' },
    { id: 'positions', label: 'Positions' },
];

const EMPLOYEE_STATUSES = [
    'Active',
    'Inactive',
    'Separated',
    'Pending Start',
    'On Extended LOA',
];

const emptyEmployee = {
    employee_number: '',
    first_name: '',
    last_name: '',
    preferred_name: '',
    job_title: '',
    telephone: '',
    email: '',
    status: 'Active',
    hire_date: '',
    separation_date: '',
    notes: '',
};

const emptyAssignment = {
    site: '',
    work_center: '',
    position: '',
    shift: '',
    supervisor: '',
    assignment_type: 'Primary',
    effective_start: new Date().toISOString().slice(0, 10),
    effective_end: '',
    reason: '',
};

const emptyWorkCenter = {
    code: '',
    name: '',
    site: '',
    supervisor: '',
    is_active: true,
    display_order: 0,
    minimum_staffing: 0,
};

const emptyGovWorkCenter = {
    code: '',
    name: '',
    site: '',
    is_active: true,
};

const emptyShift = {
    code: '',
    name: '',
    site: '',
    start_time: '07:00',
    end_time: '15:30',
    days_of_week: 'Mon-Fri',
    is_active: true,
};

const emptyPosition = {
    title: '',
    government_title: '',
    work_center: '',
    gov_work_center: '',
    required_count: 0,
    skill_rate: '',
    is_funded: true,
    is_active: true,
};

const getStyles = (theme) => {
    const isDark = theme === 'dark';
    const colors = {
        background: isDark ? '#121212' : '#f8f9fa',
        text: isDark ? '#e0e0e0' : '#212529',
        cardBg: isDark ? '#1e1e1e' : '#fff',
        border: isDark ? '#444' : '#dee2e6',
        inputBg: isDark ? '#2a2a2a' : '#fff',
        muted: isDark ? '#9aa6b2' : '#6c757d',
        link: isDark ? '#6cb2f7' : '#0d6efd',
        danger: '#dc3545',
        success: isDark ? '#4caf50' : '#198754',
        tabActive: isDark ? '#2a3a4a' : '#e7f1ff',
    };

    return {
        colors,
        page: {
            minHeight: '100vh',
            backgroundColor: colors.background,
            color: colors.text,
            fontFamily: 'sans-serif',
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 24px',
            borderBottom: `1px solid ${colors.border}`,
            backgroundColor: colors.cardBg,
            gap: '12px',
            flexWrap: 'wrap',
        },
        titleBlock: { display: 'flex', alignItems: 'center', gap: '16px' },
        title: { margin: 0, fontSize: '24px' },
        subtitle: { margin: '4px 0 0', color: colors.muted, fontSize: '14px' },
        content: { maxWidth: '1400px', margin: '0 auto', padding: '20px 24px 40px' },
        tabs: {
            display: 'flex',
            gap: '8px',
            flexWrap: 'wrap',
            marginBottom: '20px',
        },
        tab: {
            padding: '10px 16px',
            borderRadius: '6px',
            border: `1px solid ${colors.border}`,
            backgroundColor: 'transparent',
            color: colors.text,
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 600,
        },
        tabActive: {
            backgroundColor: colors.tabActive,
            borderColor: colors.link,
            color: colors.link,
        },
        card: {
            backgroundColor: colors.cardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '20px',
        },
        statsGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: '12px',
        },
        statCard: {
            backgroundColor: isDark ? '#252525' : '#f8f9fa',
            border: `1px solid ${colors.border}`,
            borderRadius: '8px',
            padding: '16px',
            textAlign: 'center',
        },
        statNumber: { fontSize: '1.8rem', fontWeight: 700, marginBottom: '4px' },
        statLabel: { fontSize: '0.9rem', color: colors.muted },
        formGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '12px',
            alignItems: 'end',
        },
        formGroup: { display: 'flex', flexDirection: 'column', gap: '4px' },
        label: { fontWeight: 600, fontSize: '13px' },
        input: {
            padding: '10px',
            borderRadius: '4px',
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.inputBg,
            color: colors.text,
            fontSize: '14px',
        },
        buttonRow: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' },
        button: {
            padding: '10px 14px',
            backgroundColor: colors.link,
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '14px',
        },
        secondaryButton: {
            padding: '10px 14px',
            backgroundColor: 'transparent',
            color: colors.text,
            border: `1px solid ${colors.border}`,
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '14px',
        },
        dangerButton: {
            padding: '6px 10px',
            backgroundColor: colors.danger,
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '13px',
        },
        editButton: {
            padding: '6px 10px',
            backgroundColor: colors.link,
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '13px',
            marginRight: '6px',
        },
        tableWrap: { overflowX: 'auto' },
        table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
        th: {
            textAlign: 'left',
            padding: '10px 8px',
            borderBottom: `2px solid ${colors.border}`,
            whiteSpace: 'nowrap',
        },
        td: {
            padding: '10px 8px',
            borderBottom: `1px solid ${colors.border}`,
            verticalAlign: 'top',
        },
        filters: {
            display: 'flex',
            gap: '10px',
            flexWrap: 'wrap',
            marginBottom: '14px',
            alignItems: 'end',
        },
        sectionTitle: { margin: '0 0 14px', fontSize: '18px' },
        hint: { color: colors.muted, fontSize: '13px', margin: '0 0 12px' },
        badge: {
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: '999px',
            fontSize: '12px',
            fontWeight: 700,
            backgroundColor: isDark ? '#333' : '#e9ecef',
        },
        checkboxRow: { display: 'flex', alignItems: 'center', gap: '8px', height: '40px' },

        // Modal Styles
        modalOverlay: { position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
        modalContent: { backgroundColor: colors.cardBg, padding: '25px', borderRadius: '8px', width: '360px', boxShadow: '0 4px 15px rgba(0,0,0,0.2)', border: `1px solid ${colors.border}` },
        modalActions: { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' },
    };
};

function asArray(data) {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.results)) return data.results;
    return [];
}

function formatError(err) {
    const data = err?.response?.data;
    if (!data) return err?.message || 'Request failed';
    if (typeof data === 'string') return data;
    if (data.detail) return data.detail;
    if (data.error) return data.error;
    const parts = Object.entries(data).map(([key, value]) => {
        const msg = Array.isArray(value) ? value.join(' ') : String(value);
        return `${key}: ${msg}`;
    });
    return parts.join(' | ') || 'Request failed';
}

function matchesActiveFilter(isActive, filterValue) {
    if (filterValue === 'yes') return !!isActive;
    if (filterValue === 'no') return !isActive;
    return true;
}

function ManningPage({ theme }) {
    const styles = useMemo(() => getStyles(theme), [theme]);
    const [tab, setTab] = useState('dashboard');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploadingEmployees, setUploadingEmployees] = useState(false);
    const [alert, setAlert] = useState({ show: false, message: '', type: '' });
    const employeeUploadInputRef = useRef(null);

    const [sites, setSites] = useState([]);
    const [summary, setSummary] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [supervisorCandidates, setSupervisorCandidates] = useState([]);
    const [workCenters, setWorkCenters] = useState([]);
    const [govWorkCenters, setGovWorkCenters] = useState([]);
    const [shifts, setShifts] = useState([]);
    const [positions, setPositions] = useState([]);

    const [employeeForm, setEmployeeForm] = useState(emptyEmployee);
    const [assignmentForm, setAssignmentForm] = useState(emptyAssignment);
    const [editingEmployeeId, setEditingEmployeeId] = useState(null);
    const [editingAssignmentId, setEditingAssignmentId] = useState(null);
    const [includeAssignment, setIncludeAssignment] = useState(true);

    const [workCenterForm, setWorkCenterForm] = useState(emptyWorkCenter);
    const [editingWorkCenterId, setEditingWorkCenterId] = useState(null);

    const [govWorkCenterForm, setGovWorkCenterForm] = useState(emptyGovWorkCenter);
    const [editingGovWorkCenterId, setEditingGovWorkCenterId] = useState(null);

    const [shiftForm, setShiftForm] = useState(emptyShift);
    const [editingShiftId, setEditingShiftId] = useState(null);

    const [positionForm, setPositionForm] = useState(emptyPosition);
    const [editingPositionId, setEditingPositionId] = useState(null);
    const [positionSort, setPositionSort] = useState({ key: 'title', dir: 'asc' });

    // Separation Modal State
    const [separatingEmployee, setSeparatingEmployee] = useState(null);
    const [separationDate, setSeparationDate] = useState('');

    const [workCenterFilters, setWorkCenterFilters] = useState({
        search: '',
        site: '',
        active: '',
    });
    const [govWorkCenterFilters, setGovWorkCenterFilters] = useState({
        search: '',
        site: '',
        active: '',
    });
    const [shiftFilters, setShiftFilters] = useState({
        search: '',
        site: '',
        active: '',
    });
    const [positionFilters, setPositionFilters] = useState({
        search: '',
        site: '',
        work_center: '',
        gov_work_center: '',
        active: '',
        funded: '',
    });

    const filteredWorkCenters = useMemo(() => {
        const search = workCenterFilters.search.trim().toLowerCase();
        return workCenters.filter((wc) => {
            if (workCenterFilters.site && String(wc.site) !== String(workCenterFilters.site)) {
                return false;
            }
            if (!matchesActiveFilter(wc.is_active, workCenterFilters.active)) return false;
            if (!search) return true;
            const text = `${wc.code || ''} ${wc.name || ''} ${wc.site_name || ''}`.toLowerCase();
            return text.includes(search);
        });
    }, [workCenters, workCenterFilters]);

    const filteredGovWorkCenters = useMemo(() => {
        const search = govWorkCenterFilters.search.trim().toLowerCase();
        return govWorkCenters.filter((gwc) => {
            if (govWorkCenterFilters.site && String(gwc.site) !== String(govWorkCenterFilters.site)) {
                return false;
            }
            if (!matchesActiveFilter(gwc.is_active, govWorkCenterFilters.active)) return false;
            if (!search) return true;
            const text = `${gwc.code || ''} ${gwc.name || ''} ${gwc.site_name || ''}`.toLowerCase();
            return text.includes(search);
        });
    }, [govWorkCenters, govWorkCenterFilters]);

    const filteredShifts = useMemo(() => {
        const search = shiftFilters.search.trim().toLowerCase();
        return shifts.filter((shift) => {
            if (shiftFilters.site && String(shift.site) !== String(shiftFilters.site)) {
                return false;
            }
            if (!matchesActiveFilter(shift.is_active, shiftFilters.active)) return false;
            if (!search) return true;
            const text = `${shift.code || ''} ${shift.name || ''} ${shift.site_name || ''} ${shift.days_of_week || ''}`.toLowerCase();
            return text.includes(search);
        });
    }, [shifts, shiftFilters]);

    const togglePositionSort = (key) => {
        setPositionSort((prev) =>
            prev.key === key
                ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
                : { key, dir: 'asc' }
        );
    };

    const sortedPositions = useMemo(() => {
        const search = positionFilters.search.trim().toLowerCase();
        const filtered = positions.filter((pos) => {
            if (positionFilters.site && String(pos.site) !== String(positionFilters.site)) {
                return false;
            }
            if (
                positionFilters.work_center &&
                String(pos.work_center) !== String(positionFilters.work_center)
            ) {
                return false;
            }
            if (
                positionFilters.gov_work_center &&
                String(pos.gov_work_center) !== String(positionFilters.gov_work_center)
            ) {
                return false;
            }
            if (!matchesActiveFilter(pos.is_active, positionFilters.active)) return false;
            if (positionFilters.funded === 'yes' && !pos.is_funded) return false;
            if (positionFilters.funded === 'no' && pos.is_funded) return false;
            if (!search) return true;
            const text = [
                pos.title,
                pos.government_title,
                pos.work_center_code,
                pos.work_center_name,
                pos.gov_work_center_code,
                pos.gov_work_center_name,
                pos.site_name,
                pos.skill_rate,
            ]
                .join(' ')
                .toLowerCase();
            return text.includes(search);
        });

        const { key, dir } = positionSort;
        const valueOf = (pos) => {
            switch (key) {
                case 'work_center':
                    return `${pos.work_center_code || ''} ${pos.work_center_name || ''}`.toLowerCase();
                case 'gov_work_center':
                    return `${pos.gov_work_center_code || ''} ${pos.gov_work_center_name || ''}`.toLowerCase();
                case 'site':
                    return (pos.site_name || '').toLowerCase();
                case 'required':
                    return Number(pos.required_count) || 0;
                case 'government_title':
                    return (pos.government_title || '').toLowerCase();
                case 'skill_rate':
                    return (pos.skill_rate || '').toLowerCase();
                case 'funded':
                    return pos.is_funded ? 1 : 0;
                case 'active':
                    return pos.is_active ? 1 : 0;
                case 'title':
                default:
                    return (pos.title || '').toLowerCase();
            }
        };
        const sorted = [...filtered].sort((a, b) => {
            const av = valueOf(a);
            const bv = valueOf(b);
            if (av < bv) return -1;
            if (av > bv) return 1;
            return 0;
        });
        return dir === 'desc' ? sorted.reverse() : sorted;
    }, [positions, positionSort, positionFilters]);

    const [employeeFilters, setEmployeeFilters] = useState({
        search: '',
        status: 'Active',
        site: '',
        work_center: '',
    });

    const showAlert = (message, type = 'success') => {
        setAlert({ show: true, message, type });
    };

    const loadLookupData = useCallback(async () => {
        const [sitesRes, wcRes, govWcRes, shiftRes, posRes] = await Promise.all([
            api.get('/sites/'),
            api.get('/manning-work-centers/'),
            api.get('/manning-gov-work-centers/'),
            api.get('/manning-shifts/'),
            api.get('/manning-positions/'),
        ]);
        setSites(asArray(sitesRes.data));
        setWorkCenters(asArray(wcRes.data));
        setGovWorkCenters(asArray(govWcRes.data));
        setShifts(asArray(shiftRes.data));
        setPositions(asArray(posRes.data));
    }, []);

    const loadSummary = useCallback(async () => {
        const res = await api.get('/employees/summary/');
        setSummary(res.data);
    }, []);

    const loadEmployees = useCallback(async () => {
        const params = {};
        if (employeeFilters.search) params.search = employeeFilters.search;
        if (employeeFilters.status) params.status = employeeFilters.status;
        if (employeeFilters.site) params.site = employeeFilters.site;
        if (employeeFilters.work_center) params.work_center = employeeFilters.work_center;
        const res = await api.get('/employees/', { params });
        setEmployees(asArray(res.data));
    }, [employeeFilters]);

    const loadSupervisorCandidates = useCallback(async () => {
        // Full roster for supervisor picks — independent of directory filters / shift.
        const res = await api.get('/employees/');
        setSupervisorCandidates(
            asArray(res.data).filter((e) =>
                ['Active', 'On Extended LOA', 'Pending Start'].includes(e.status)
            )
        );
    }, []);

    const refreshAll = useCallback(async () => {
        setLoading(true);
        try {
            await Promise.all([
                loadLookupData(),
                loadSummary(),
                loadEmployees(),
                loadSupervisorCandidates(),
            ]);
        } catch (err) {
            showAlert(formatError(err), 'error');
        } finally {
            setLoading(false);
        }
    }, [loadLookupData, loadSummary, loadEmployees, loadSupervisorCandidates]);

    useEffect(() => {
        refreshAll();
    }, [refreshAll]);

    const filteredWorkCentersForSite = useMemo(() => {
        if (!assignmentForm.site) return workCenters.filter((wc) => wc.is_active);
        return workCenters.filter(
            (wc) => String(wc.site) === String(assignmentForm.site) && wc.is_active
        );
    }, [workCenters, assignmentForm.site]);

    const filteredShiftsForSite = useMemo(() => {
        if (!assignmentForm.site) return shifts.filter((s) => s.is_active);
        return shifts.filter(
            (s) => String(s.site) === String(assignmentForm.site) && s.is_active
        );
    }, [shifts, assignmentForm.site]);

    const filteredPositionsForWc = useMemo(() => {
        if (!assignmentForm.work_center) return positions.filter((p) => p.is_active);
        return positions.filter(
            (p) => String(p.work_center) === String(assignmentForm.work_center) && p.is_active
        );
    }, [positions, assignmentForm.work_center]);

    const activePositions = useMemo(
        () => positions.filter((p) => p.is_active),
        [positions]
    );

    const selectedAssignmentPosition = useMemo(
        () =>
            positions.find((p) => String(p.id) === String(assignmentForm.position)) || null,
        [positions, assignmentForm.position]
    );

    const applyPositionSelection = (positionId, { syncAssignment = true } = {}) => {
        const pos = positions.find((p) => String(p.id) === String(positionId));
        if (!pos) {
            setEmployeeForm((prev) => ({ ...prev, job_title: '' }));
            if (syncAssignment) {
                setAssignmentForm((prev) => ({ ...prev, position: '' }));
            }
            return;
        }
        setEmployeeForm((prev) => ({
            ...prev,
            // Keep job_title synced to internal title for directory/reports.
            job_title: pos.title || '',
        }));
        if (syncAssignment) {
            setAssignmentForm((prev) => ({
                ...prev,
                site: pos.site || prev.site,
                work_center: pos.work_center || prev.work_center,
                position: String(pos.id),
            }));
        }
    };

    const resetEmployeeForm = () => {
        setEmployeeForm(emptyEmployee);
        setAssignmentForm({
            ...emptyAssignment,
            effective_start: new Date().toISOString().slice(0, 10),
        });
        setEditingEmployeeId(null);
        setEditingAssignmentId(null);
        setIncludeAssignment(true);
    };

    const startEditEmployee = async (employee) => {
        try {
            const res = await api.get(`/employees/${employee.id}/`);
            const detail = res.data;
            setEditingEmployeeId(detail.id);
            setEmployeeForm({
                employee_number: detail.employee_number || '',
                first_name: detail.first_name || '',
                last_name: detail.last_name || '',
                preferred_name: detail.preferred_name || '',
                job_title: detail.job_title || '',
                telephone: detail.telephone || '',
                email: detail.email || '',
                status: detail.status || 'Active',
                hire_date: detail.hire_date || '',
                separation_date: detail.separation_date || '',
                notes: detail.notes || '',
            });
            const current = detail.current_assignment;
            if (current) {
                setEditingAssignmentId(current.id);
                setIncludeAssignment(true);
                setAssignmentForm({
                    site: current.site || '',
                    work_center: current.work_center || '',
                    position: current.position || '',
                    shift: current.shift || '',
                    supervisor: current.supervisor || '',
                    assignment_type: current.assignment_type || 'Primary',
                    effective_start:
                        current.effective_start || new Date().toISOString().slice(0, 10),
                    effective_end: current.effective_end || '',
                    reason: current.reason || '',
                });
                if (current.position_title) {
                    setEmployeeForm((prev) => ({
                        ...prev,
                        job_title: current.position_title || prev.job_title || '',
                    }));
                }
            } else {
                setEditingAssignmentId(null);
                setIncludeAssignment(true);
                setAssignmentForm({
                    ...emptyAssignment,
                    effective_start: new Date().toISOString().slice(0, 10),
                });
            }
            setTab('employees');
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (err) {
            showAlert(formatError(err), 'error');
        }
    };

    const saveEmployee = async (event) => {
        event.preventDefault();
        setSaving(true);
        try {
            const selectedPos = positions.find(
                (p) => String(p.id) === String(assignmentForm.position)
            );
            const payload = {
                ...employeeForm,
                hire_date: employeeForm.hire_date || null,
                separation_date: employeeForm.separation_date || null,
                preferred_name: employeeForm.preferred_name || '',
                job_title: selectedPos?.title || employeeForm.job_title || '',
                telephone: employeeForm.telephone || '',
                email: employeeForm.email || '',
                notes: employeeForm.notes || '',
            };

            let employeeId = editingEmployeeId;
            if (editingEmployeeId) {
                await api.patch(`/employees/${editingEmployeeId}/`, payload);
            } else {
                const created = await api.post('/employees/', payload);
                employeeId = created.data.id;
            }

            const assignmentPayload = {
                site: Number(assignmentForm.site),
                work_center: Number(assignmentForm.work_center),
                position: assignmentForm.position ? Number(assignmentForm.position) : null,
                shift: assignmentForm.shift ? Number(assignmentForm.shift) : null,
                supervisor: assignmentForm.supervisor ? Number(assignmentForm.supervisor) : null,
                assignment_type: assignmentForm.assignment_type || 'Primary',
                effective_start: assignmentForm.effective_start,
                effective_end: assignmentForm.effective_end || null,
                reason: assignmentForm.reason || '',
            };

            if (
                includeAssignment &&
                assignmentForm.site &&
                assignmentForm.work_center &&
                payload.status !== 'Separated' // <--- ADD THIS LINE
            ) {
                if (editingAssignmentId) {
                    await api.patch(
                        `/employee-assignments/${editingAssignmentId}/`,
                        assignmentPayload
                    );
                    showAlert('Employee and assignment updated');
                } else {
                    await api.post('/employee-assignments/', {
                        employee: employeeId,
                        ...assignmentPayload,
                    });
                    showAlert(
                        editingEmployeeId
                            ? 'Employee updated and assignment added'
                            : 'Employee and assignment saved'
                    );
                }
            } else if (editingEmployeeId) {
                showAlert('Employee updated');
            } else {
                showAlert('Employee created');
            }

            resetEmployeeForm();
            await Promise.all([
                loadEmployees(),
                loadSupervisorCandidates(),
                loadSummary(),
                loadLookupData(),
            ]);
        } catch (err) {
            showAlert(formatError(err), 'error');
        } finally {
            setSaving(false);
        }
    };

    // Modal Initiation
    const initiateSeparation = (employee) => {
        setSeparatingEmployee(employee);
        setSeparationDate(new Date().toISOString().slice(0, 10)); // Default to today
    };

    // Submits the Separation from the Modal
    const confirmSeparation = async (e) => {
        e.preventDefault();
        if (!separatingEmployee) return;

        try {
            await api.patch(`/employees/${separatingEmployee.id}/`, {
                status: 'Separated',
                separation_date: separationDate || new Date().toISOString().slice(0, 10),
            });
            showAlert('Employee marked Separated');
            setSeparatingEmployee(null);
            await Promise.all([loadEmployees(), loadSupervisorCandidates(), loadSummary()]);
        } catch (err) {
            showAlert(formatError(err), 'error');
        }
    };

    const deleteEmployee = async (employee) => {
        const label = employee.display_name || employee.employee_number;
        if (
            !window.confirm(
                `Permanently delete ${label}? This removes the employee and related leave/assignment history and cannot be undone. Use Separate instead if they actually worked.`
            )
        ) {
            return;
        }
        try {
            await api.delete(`/employees/${employee.id}/`);
            showAlert('Employee deleted');
            if (editingEmployeeId === employee.id) {
                resetEmployeeForm();
            }
            await Promise.all([loadEmployees(), loadSupervisorCandidates(), loadSummary()]);
        } catch (err) {
            showAlert(formatError(err), 'error');
        }
    };

    const uploadEmployeesExcel = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        setUploadingEmployees(true);
        try {
            const data = new FormData();
            data.append('file', file);
            const res = await api.post('/employees/upload-excel/', data, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            const result = res.data || {};
            const errorHint =
                result.error_count > 0
                    ? ` ${result.error_count} row error(s)` +
                      (result.errors?.[0]
                          ? ` (e.g. row ${result.errors[0].row}: ${result.errors[0].error})`
                          : '')
                    : '';
            showAlert(
                `Import complete: ${result.created || 0} created, ${result.updated || 0} updated, ` +
                    `${result.work_centers_created || 0} work center(s), ` +
                    `${result.assignments_created || 0} assignment(s) created, ` +
                    `${result.assignments_updated || 0} assignment(s) updated.${errorHint}`,
                result.error_count > 0 ? 'error' : 'success'
            );
            await Promise.all([
                loadEmployees(),
                loadSupervisorCandidates(),
                loadSummary(),
                loadLookupData(),
            ]);
        } catch (err) {
            showAlert(formatError(err), 'error');
        } finally {
            setUploadingEmployees(false);
            if (event.target) event.target.value = '';
        }
    };

    const saveWorkCenter = async (event) => {
        event.preventDefault();
        setSaving(true);
        try {
            const payload = {
                ...workCenterForm,
                site: Number(workCenterForm.site),
                supervisor: workCenterForm.supervisor ? Number(workCenterForm.supervisor) : null,
                display_order: Number(workCenterForm.display_order) || 0,
                minimum_staffing: Number(workCenterForm.minimum_staffing) || 0,
            };
            if (editingWorkCenterId) {
                await api.patch(`/manning-work-centers/${editingWorkCenterId}/`, payload);
                showAlert('Work center updated');
            } else {
                await api.post('/manning-work-centers/', payload);
                showAlert('Work center created');
            }
            setWorkCenterForm(emptyWorkCenter);
            setEditingWorkCenterId(null);
            await Promise.all([loadLookupData(), loadSummary()]);
        } catch (err) {
            showAlert(formatError(err), 'error');
        } finally {
            setSaving(false);
        }
    };

    const deleteWorkCenter = async (wc) => {
        const label = `${wc.code} — ${wc.name}`;
        if (
            !window.confirm(
                `Permanently delete work center ${label}? Positions under it will also be removed. This cannot be undone.`
            )
        ) {
            return;
        }
        try {
            await api.delete(`/manning-work-centers/${wc.id}/`);
            showAlert('Work center deleted');
            if (editingWorkCenterId === wc.id) {
                setEditingWorkCenterId(null);
                setWorkCenterForm(emptyWorkCenter);
            }
            await Promise.all([loadLookupData(), loadSummary()]);
        } catch (err) {
            showAlert(formatError(err), 'error');
        }
    };

    const saveGovWorkCenter = async (event) => {
        event.preventDefault();
        setSaving(true);
        try {
            const payload = {
                ...govWorkCenterForm,
                site: Number(govWorkCenterForm.site),
            };
            if (editingGovWorkCenterId) {
                await api.patch(`/manning-gov-work-centers/${editingGovWorkCenterId}/`, payload);
                showAlert('Gov work center updated');
            } else {
                await api.post('/manning-gov-work-centers/', payload);
                showAlert('Gov work center created');
            }
            setGovWorkCenterForm(emptyGovWorkCenter);
            setEditingGovWorkCenterId(null);
            await Promise.all([loadLookupData(), loadSummary()]);
        } catch (err) {
            showAlert(formatError(err), 'error');
        } finally {
            setSaving(false);
        }
    };

    const deleteGovWorkCenter = async (gwc) => {
        const label = `${gwc.code} — ${gwc.name}`;
        if (
            !window.confirm(
                `Permanently delete gov work center ${label}? This cannot be undone.`
            )
        ) {
            return;
        }
        try {
            await api.delete(`/manning-gov-work-centers/${gwc.id}/`);
            showAlert('Gov work center deleted');
            if (editingGovWorkCenterId === gwc.id) {
                setEditingGovWorkCenterId(null);
                setGovWorkCenterForm(emptyGovWorkCenter);
            }
            await Promise.all([loadLookupData(), loadSummary()]);
        } catch (err) {
            showAlert(formatError(err), 'error');
        }
    };

    const saveShift = async (event) => {
        event.preventDefault();
        setSaving(true);
        try {
            const payload = {
                ...shiftForm,
                site: Number(shiftForm.site),
            };
            if (editingShiftId) {
                await api.patch(`/manning-shifts/${editingShiftId}/`, payload);
                showAlert('Shift updated');
            } else {
                await api.post('/manning-shifts/', payload);
                showAlert('Shift created');
            }
            setShiftForm(emptyShift);
            setEditingShiftId(null);
            await Promise.all([loadLookupData(), loadSummary()]);
        } catch (err) {
            showAlert(formatError(err), 'error');
        } finally {
            setSaving(false);
        }
    };

    const deleteShift = async (shift) => {
        const label = `${shift.code} — ${shift.name}`;
        if (
            !window.confirm(
                `Permanently delete shift ${label}? Assignments using it will clear the shift. This cannot be undone.`
            )
        ) {
            return;
        }
        try {
            await api.delete(`/manning-shifts/${shift.id}/`);
            showAlert('Shift deleted');
            if (editingShiftId === shift.id) {
                setEditingShiftId(null);
                setShiftForm(emptyShift);
            }
            await Promise.all([loadLookupData(), loadSummary()]);
        } catch (err) {
            showAlert(formatError(err), 'error');
        }
    };

    const savePosition = async (event) => {
        event.preventDefault();
        setSaving(true);
        try {
            const payload = {
                ...positionForm,
                work_center: Number(positionForm.work_center),
                gov_work_center: Number(positionForm.gov_work_center),
                required_count: Math.max(0, Number(positionForm.required_count) || 0),
            };
            if (editingPositionId) {
                await api.patch(`/manning-positions/${editingPositionId}/`, payload);
                showAlert('Position updated');
            } else {
                await api.post('/manning-positions/', payload);
                showAlert('Position created');
            }
            setPositionForm(emptyPosition);
            setEditingPositionId(null);
            await Promise.all([loadLookupData(), loadSummary()]);
        } catch (err) {
            showAlert(formatError(err), 'error');
        } finally {
            setSaving(false);
        }
    };

    const deletePosition = async (pos) => {
        const label = pos.title || `Position #${pos.id}`;
        if (
            !window.confirm(
                `Permanently delete position / billet "${label}"? Assignments using it will clear the position. This cannot be undone.`
            )
        ) {
            return;
        }
        try {
            await api.delete(`/manning-positions/${pos.id}/`);
            showAlert('Position deleted');
            if (editingPositionId === pos.id) {
                setEditingPositionId(null);
                setPositionForm(emptyPosition);
            }
            await Promise.all([loadLookupData(), loadSummary()]);
        } catch (err) {
            showAlert(formatError(err), 'error');
        }
    };

    const supervisorOptions = useMemo(() => {
        const list = supervisorCandidates
            .filter((e) => e.id !== editingEmployeeId)
            .slice()
            .sort((a, b) =>
                (a.display_name || '').localeCompare(b.display_name || '', undefined, {
                    sensitivity: 'base',
                })
            );
        return list;
    }, [supervisorCandidates, editingEmployeeId]);

    if (loading && !summary) {
        return (
            <div style={styles.page}>
                <div style={{ padding: '40px', textAlign: 'center' }}>
                    <Spinner />
                </div>
            </div>
        );
    }

    return (
        <div style={styles.page}>
            <CustomAlert
                show={alert.show}
                message={alert.message}
                type={alert.type}
                onClose={() => setAlert({ show: false, message: '', type: '' })}
                theme={theme}
            />

            <div style={styles.header}>
                <div style={styles.titleBlock}>
                    <HamburgerMenu theme={theme} />
                    <div>
                        <h1 style={styles.title}>Manning</h1>
                        <p style={styles.subtitle}>
                            Daily availability, org chart, leave, employees, and assignments
                        </p>
                    </div>
                </div>
                <button type="button" style={styles.secondaryButton} onClick={refreshAll}>
                    Refresh
                </button>
            </div>

            <div style={styles.content}>
                <div style={styles.tabs}>
                    {TABS.map((item) => (
                        <button
                            key={item.id}
                            type="button"
                            style={{
                                ...styles.tab,
                                ...(tab === item.id ? styles.tabActive : {}),
                            }}
                            onClick={() => setTab(item.id)}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>

                {tab === 'dashboard' && (
                    <div style={styles.card}>
                        <h2 style={styles.sectionTitle}>Manning summary</h2>
                        <p style={styles.hint}>
                            Manning operations through reports are live. Use Reports for roster,
                            daily manning, leave calendar, vacancies, leave impact, and audit history.
                            Costpoint / NALCOMIS imports come next.
                        </p>
                        <div style={styles.statsGrid}>
                            {[
                                ['Active employees', summary?.active_employees],
                                ['Available today', summary?.available_today],
                                ['Assigned active', summary?.assigned_active],
                                ['Temp assignments today', summary?.temporary_assignments_today],
                                ['Pending leave approvals', summary?.pending_leave_approvals],
                                ['Active absences today', summary?.active_absences_today],
                                ['Below minimum WCs', summary?.work_centers_below_minimum],
                                ['Work centers', summary?.work_centers],
                                ['Separated (history)', summary?.separated_employees],
                            ].map(([label, value]) => (
                                <div key={label} style={styles.statCard}>
                                    <div style={styles.statNumber}>{value ?? '—'}</div>
                                    <div style={styles.statLabel}>{label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {tab === 'daily' && (
                    <div style={styles.card}>
                        <h2 style={styles.sectionTitle}>Daily availability</h2>
                        <ManningDailyAvailability
                            theme={theme}
                            sites={sites}
                            workCenters={workCenters}
                            shifts={shifts}
                            positions={positions}
                            onSelectEmployee={(emp) => startEditEmployee(emp)}
                            onNotify={showAlert}
                            onChanged={async () => {
                                await Promise.all([loadSummary(), loadEmployees(), loadLookupData()]);
                            }}
                        />
                    </div>
                )}

                {tab === 'org-chart' && (
                    <div style={styles.card}>
                        <h2 style={styles.sectionTitle}>Organization chart</h2>
                        <ManningOrgChart
                            theme={theme}
                            sites={sites}
                            workCenters={workCenters}
                            onSelectEmployee={(emp) => startEditEmployee(emp)}
                        />
                    </div>
                )}

                {tab === 'functional-org' && (
                    <div style={styles.card}>
                        <h2 style={styles.sectionTitle}>Functional organization</h2>
                        <ManningFunctionalOrg
                            theme={theme}
                            sites={sites}
                            workCenters={workCenters}
                            onSelectEmployee={(emp) => startEditEmployee(emp)}
                        />
                    </div>
                )}

                {tab === 'leave' && (
                    <div style={styles.card}>
                        <h2 style={styles.sectionTitle}>Leave chits & LOA</h2>
                        <ManningLeavePanel
                            theme={theme}
                            onNotify={showAlert}
                            onChanged={async () => {
                                await Promise.all([loadSummary(), loadEmployees()]);
                            }}
                        />
                    </div>
                )}

                {tab === 'reports' && (
                    <div style={styles.card}>
                        <h2 style={styles.sectionTitle}>Manning reports</h2>
                        <ManningReportsPanel
                            theme={theme}
                            sites={sites}
                            onNotify={showAlert}
                        />
                    </div>
                )}

                {tab === 'employees' && (
                    <>
                        <div style={styles.card}>
                            <h2 style={styles.sectionTitle}>
                                {editingEmployeeId ? 'Edit employee' : 'Add employee'}
                            </h2>
                            <form onSubmit={saveEmployee}>
                                <div style={styles.formGrid}>
                                    {[
                                        ['employee_number', 'Employee number', 'text', true],
                                        ['first_name', 'First name', 'text', true],
                                        ['last_name', 'Last name', 'text', true],
                                        ['preferred_name', 'Preferred name', 'text', false],
                                        ['telephone', 'Telephone', 'text', false],
                                        ['email', 'Email', 'email', false],
                                        ['hire_date', 'Hire date', 'date', false],
                                        ['separation_date', 'Separation date', 'date', false],
                                    ].map(([key, label, type, required]) => (
                                        <div key={key} style={styles.formGroup}>
                                            <label style={styles.label}>{label}</label>
                                            <input
                                                style={styles.input}
                                                type={type}
                                                required={required}
                                                value={employeeForm[key]}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    setEmployeeForm((prev) => {
                                                        const newState = { ...prev, [key]: val };

                                                        if (key === 'separation_date') {
                                                            if (val) {
                                                                newState.status = 'Separated';
                                                            } else if (prev.status === 'Separated') {
                                                                // Cleared the date -> reactivate employee
                                                                newState.status = 'Active';
                                                            }
                                                        }

                                                        return newState;
                                                    });

                                                    // Toggle assignment section based on date presence
                                                    if (key === 'separation_date') {
                                                        setIncludeAssignment(!val);
                                                    }
                                                }}
                                            />
                                        </div>
                                    ))}
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>Internal title</label>
                                        <select
                                            style={styles.input}
                                            value={assignmentForm.position || ''}
                                            onChange={(e) =>
                                                applyPositionSelection(e.target.value)
                                            }
                                        >
                                            <option value="">Select internal title</option>
                                            {activePositions.map((pos) => (
                                                <option key={pos.id} value={pos.id}>
                                                    {pos.site_name}: {pos.work_center_code} — {pos.title}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>Status</label>
                                        <select
                                            style={styles.input}
                                            value={employeeForm.status}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setEmployeeForm((prev) => ({
                                                    ...prev,
                                                    status: val,
                                                    // Clear separation date if switched back to Active
                                                    separation_date: val === 'Active' ? '' : prev.separation_date,
                                                }));

                                                setIncludeAssignment(val !== 'Separated');
                                            }}
                                        >
                                            {EMPLOYEE_STATUSES.map((status) => (
                                                <option key={status} value={status}>
                                                    {status}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
                                        <label style={styles.label}>Notes</label>
                                        <textarea
                                            style={{ ...styles.input, minHeight: '70px' }}
                                            value={employeeForm.notes}
                                            onChange={(e) =>
                                                setEmployeeForm((prev) => ({
                                                    ...prev,
                                                    notes: e.target.value,
                                                }))
                                            }
                                        />
                                    </div>
                                </div>

                                <div style={{ marginTop: '18px' }}>
                                    {editingAssignmentId ? (
                                        <p style={styles.hint}>
                                            Editing current assignment — site, work center, internal
                                            title, shift, and supervisor changes are saved with the
                                            employee update.
                                        </p>
                                    ) : (
                                        <div style={styles.checkboxRow}>
                                            <input
                                                id="include-assignment"
                                                type="checkbox"
                                                checked={includeAssignment}
                                                onChange={(e) =>
                                                    setIncludeAssignment(e.target.checked)
                                                }
                                            />
                                            <label
                                                htmlFor="include-assignment"
                                                style={styles.label}
                                            >
                                                {editingEmployeeId
                                                    ? 'Add a primary assignment'
                                                    : 'Create primary assignment with this employee'}
                                            </label>
                                        </div>
                                    )}
                                </div>

                                {(includeAssignment || editingAssignmentId) && (
                                    <div style={{ marginTop: '12px' }}>
                                        <p style={styles.hint}>
                                            {editingAssignmentId
                                                ? 'Current primary assignment details.'
                                                : 'Permanent home organization. Temporary loans can be managed on Daily Availability.'}
                                                <br />
                                                <br />
                                            {' '}<strong>Note:</strong> Supervisors can be assigned to any shift or work center.
                                        </p><br />
                                        <div style={styles.formGrid}>
                                            <div style={styles.formGroup}>
                                                <label style={styles.label}>Site</label>
                                                <select
                                                    style={styles.input}
                                                    required={includeAssignment}
                                                    value={assignmentForm.site}
                                                    onChange={(e) =>
                                                        setAssignmentForm((prev) => ({
                                                            ...prev,
                                                            site: e.target.value,
                                                            work_center: '',
                                                            shift: '',
                                                            position: '',
                                                        }))
                                                    }
                                                >
                                                    <option value="">Select site</option>
                                                    {sites.map((site) => (
                                                        <option key={site.id} value={site.id}>
                                                            {site.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div style={styles.formGroup}>
                                                <label style={styles.label}>Work center</label>
                                                <select
                                                    style={styles.input}
                                                    required={includeAssignment}
                                                    value={assignmentForm.work_center}
                                                    onChange={(e) =>
                                                        setAssignmentForm((prev) => ({
                                                            ...prev,
                                                            work_center: e.target.value,
                                                            position: '',
                                                        }))
                                                    }
                                                >
                                                    <option value="">Select work center</option>
                                                    {filteredWorkCentersForSite.map((wc) => (
                                                        <option key={wc.id} value={wc.id}>
                                                            {wc.code} — {wc.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div style={styles.formGroup}>
                                                <label style={styles.label}>Internal title</label>
                                                <select
                                                    style={styles.input}
                                                    value={assignmentForm.position}
                                                    onChange={(e) =>
                                                        applyPositionSelection(e.target.value, {
                                                            syncAssignment: true,
                                                        })
                                                    }
                                                >
                                                    <option value="">Optional</option>
                                                    {filteredPositionsForWc.map((pos) => (
                                                        <option key={pos.id} value={pos.id}>
                                                            {pos.title}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div style={styles.formGroup}>
                                                <label style={styles.label}>Government title</label>
                                                <input
                                                    style={{
                                                        ...styles.input,
                                                        opacity: 0.9,
                                                        backgroundColor:
                                                            theme === 'dark' ? '#2a2a2a' : '#f3f4f6',
                                                    }}
                                                    readOnly
                                                    value={
                                                        selectedAssignmentPosition?.government_title ||
                                                        ''
                                                    }
                                                    placeholder="Auto-fills from internal title"
                                                />
                                            </div>
                                            <div style={styles.formGroup}>
                                                <label style={styles.label}>Shift</label>
                                                <select
                                                    style={styles.input}
                                                    value={assignmentForm.shift}
                                                    onChange={(e) =>
                                                        setAssignmentForm((prev) => ({
                                                            ...prev,
                                                            shift: e.target.value,
                                                        }))
                                                    }
                                                >
                                                    <option value="">Optional</option>
                                                    {filteredShiftsForSite.map((shift) => (
                                                        <option key={shift.id} value={shift.id}>
                                                            {shift.code} — {shift.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div style={styles.formGroup}>
                                                    <label style={styles.label}>Supervisor</label>
                                                    <select
                                                        style={styles.input}
                                                        value={assignmentForm.supervisor}
                                                        onChange={(e) =>
                                                            setAssignmentForm((prev) => ({
                                                                ...prev,
                                                                supervisor: e.target.value,
                                                            }))
                                                        }
                                                    >
                                                        <option value="">Optional</option>
                                                        {supervisorOptions.map((emp) => (
                                                            <option key={emp.id} value={emp.id}>
                                                                {emp.display_name} ({emp.employee_number})
                                                                {emp.current_shift
                                                                    ? ` — ${emp.current_shift}`
                                                                    : ''}
                                                                {emp.current_work_center
                                                                    ? ` · ${emp.current_work_center}`
                                                                    : ''}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            <div style={styles.formGroup}>
                                                <label style={styles.label}>Effective start</label>
                                                <input
                                                    style={styles.input}
                                                    type="date"
                                                    required={includeAssignment}
                                                    value={assignmentForm.effective_start}
                                                    onChange={(e) =>
                                                        setAssignmentForm((prev) => ({
                                                            ...prev,
                                                            effective_start: e.target.value,
                                                        }))
                                                    }
                                                />
                                            </div>
                                            <div style={styles.formGroup}>
                                                <label style={styles.label}>Effective end</label>
                                                <input
                                                    style={styles.input}
                                                    type="date"
                                                    value={assignmentForm.effective_end}
                                                    onChange={(e) =>
                                                        setAssignmentForm((prev) => ({
                                                            ...prev,
                                                            effective_end: e.target.value,
                                                        }))
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div style={styles.buttonRow}>
                                    <button type="submit" style={styles.button} disabled={saving}>
                                        {saving ? 'Saving…' : editingEmployeeId ? 'Update employee' : 'Save employee'}
                                    </button>
                                    {editingEmployeeId && (
                                        <button
                                            type="button"
                                            style={styles.secondaryButton}
                                            onClick={resetEmployeeForm}
                                        >
                                            Cancel edit
                                        </button>
                                    )}
                                </div>
                            </form>
                        </div>

                        <div style={styles.card}>
                            <h2 style={styles.sectionTitle}>Upload employees (Excel)</h2>
                            <p style={styles.hint}>
                                Upload an .xlsx with headers{' '}
                                <strong>SITE</strong>, <strong>EMPL ID</strong>,{' '}
                                <strong>Last Name</strong>, <strong>First Name</strong>, and{' '}
                                <strong>Workcenter</strong>. Matching EMPL IDs update names; new
                                IDs are created. The Workcenter column creates the site work
                                center when needed and assigns the employee there.
                            </p>
                            <div style={styles.buttonRow}>
                                <input
                                    ref={employeeUploadInputRef}
                                    type="file"
                                    accept=".xlsx,.xlsm"
                                    style={{ display: 'none' }}
                                    onChange={uploadEmployeesExcel}
                                />
                                <button
                                    type="button"
                                    style={styles.button}
                                    disabled={uploadingEmployees}
                                    onClick={() => employeeUploadInputRef.current?.click()}
                                >
                                    {uploadingEmployees ? 'Uploading…' : 'Choose Excel file'}
                                </button>
                            </div>
                        </div>

                        <div style={styles.card}>
                            <h2 style={styles.sectionTitle}>Employee directory</h2>
                            <div style={styles.filters}>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Search</label>
                                    <input
                                        style={styles.input}
                                        value={employeeFilters.search}
                                        placeholder="Name, number, phone…"
                                        onChange={(e) =>
                                            setEmployeeFilters((prev) => ({
                                                ...prev,
                                                search: e.target.value,
                                            }))
                                        }
                                    />
                                </div>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Status</label>
                                    <select
                                        style={styles.input}
                                        value={employeeFilters.status}
                                        onChange={(e) =>
                                            setEmployeeFilters((prev) => ({
                                                ...prev,
                                                status: e.target.value,
                                            }))
                                        }
                                    >
                                        <option value="">All</option>
                                        {EMPLOYEE_STATUSES.map((status) => (
                                            <option key={status} value={status}>
                                                {status}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Site</label>
                                    <select
                                        style={styles.input}
                                        value={employeeFilters.site}
                                        onChange={(e) =>
                                            setEmployeeFilters((prev) => ({
                                                ...prev,
                                                site: e.target.value,
                                                work_center: '',
                                            }))
                                        }
                                    >
                                        <option value="">All sites</option>
                                        {sites.map((site) => (
                                            <option key={site.id} value={site.id}>
                                                {site.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Work center</label>
                                    <select
                                        style={styles.input}
                                        value={employeeFilters.work_center}
                                        onChange={(e) =>
                                            setEmployeeFilters((prev) => ({
                                                ...prev,
                                                work_center: e.target.value,
                                            }))
                                        }
                                    >
                                        <option value="">All work centers</option>
                                        {workCenters
                                            .filter(
                                                (wc) =>
                                                    !employeeFilters.site ||
                                                    String(wc.site) === String(employeeFilters.site)
                                            )
                                            .map((wc) => (
                                                <option key={wc.id} value={wc.id}>
                                                    {wc.code} — {wc.name}
                                                </option>
                                            ))}
                                    </select>
                                </div>
                                <button type="button" style={styles.button} onClick={loadEmployees}>
                                    Apply filters
                                </button>
                            </div>

                            <div style={styles.tableWrap}>
                                <table style={styles.table}>
                                    <thead>
                                        <tr>
                                            <th style={styles.th}>Employee #</th>
                                            <th style={styles.th}>Name</th>
                                            <th style={styles.th}>Title</th>
                                            <th style={styles.th}>Phone</th>
                                            <th style={styles.th}>Site</th>
                                            <th style={styles.th}>Work center</th>
                                            <th style={styles.th}>Shift</th>
                                            <th style={styles.th}>Status</th>
                                            <th style={styles.th}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {employees.length === 0 ? (
                                            <tr>
                                                <td style={styles.td} colSpan={9}>
                                                    No employees found.
                                                </td>
                                            </tr>
                                        ) : (
                                            employees.map((emp) => (
                                                <tr key={emp.id}>
                                                    <td style={styles.td}>{emp.employee_number}</td>
                                                    <td style={styles.td}>{emp.display_name}</td>
                                                    <td style={styles.td}>
                                                        {emp.current_position || emp.job_title || '—'}
                                                    </td>
                                                    <td style={styles.td}>{emp.telephone || '—'}</td>
                                                    <td style={styles.td}>{emp.current_site || '—'}</td>
                                                    <td style={styles.td}>{emp.current_work_center || '—'}</td>
                                                    <td style={styles.td}>{emp.current_shift || '—'}</td>
                                                    <td style={styles.td}>
                                                        <span style={styles.badge}>{emp.status}</span>
                                                    </td>
                                                    <td style={styles.td}>
                                                        <button
                                                            type="button"
                                                            style={styles.editButton}
                                                            onClick={() => startEditEmployee(emp)}
                                                        >
                                                            Edit
                                                        </button>
                                                        {emp.status !== 'Separated' && (
                                                            <button
                                                                type="button"
                                                                style={styles.dangerButton}
                                                                onClick={() => initiateSeparation(emp)}
                                                            >
                                                                Separate
                                                            </button>
                                                        )}
                                                        <button
                                                            type="button"
                                                            style={{
                                                                ...styles.dangerButton,
                                                                marginLeft: '6px',
                                                                backgroundColor: '#7f1d1d',
                                                            }}
                                                            onClick={() => deleteEmployee(emp)}
                                                        >
                                                            Delete
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}

                {tab === 'work-centers' && (
                    <>
                        <div style={styles.card}>
                            <h2 style={styles.sectionTitle}>
                                {editingWorkCenterId ? 'Edit work center' : 'Add work center'}
                            </h2>
                            <form onSubmit={saveWorkCenter}>
                                <div style={styles.formGrid}>
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>Code</label>
                                        <input
                                            style={styles.input}
                                            required
                                            value={workCenterForm.code}
                                            onChange={(e) =>
                                                setWorkCenterForm((prev) => ({
                                                    ...prev,
                                                    code: e.target.value,
                                                }))
                                            }
                                        />
                                    </div>
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>Name</label>
                                        <input
                                            style={styles.input}
                                            required
                                            value={workCenterForm.name}
                                            onChange={(e) =>
                                                setWorkCenterForm((prev) => ({
                                                    ...prev,
                                                    name: e.target.value,
                                                }))
                                            }
                                        />
                                    </div>
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>Site</label>
                                        <select
                                            style={styles.input}
                                            required
                                            value={workCenterForm.site}
                                            onChange={(e) =>
                                                setWorkCenterForm((prev) => ({
                                                    ...prev,
                                                    site: e.target.value,
                                                }))
                                            }
                                        >
                                            <option value="">Select site</option>
                                            {sites.map((site) => (
                                                <option key={site.id} value={site.id}>
                                                    {site.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>Minimum staffing</label>
                                        <input
                                            style={styles.input}
                                            type="number"
                                            min="0"
                                            value={workCenterForm.minimum_staffing}
                                            onChange={(e) =>
                                                setWorkCenterForm((prev) => ({
                                                    ...prev,
                                                    minimum_staffing: e.target.value,
                                                }))
                                            }
                                        />
                                    </div>
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>Display order</label>
                                        <input
                                            style={styles.input}
                                            type="number"
                                            value={workCenterForm.display_order}
                                            onChange={(e) =>
                                                setWorkCenterForm((prev) => ({
                                                    ...prev,
                                                    display_order: e.target.value,
                                                }))
                                            }
                                        />
                                    </div>
                                    <div style={styles.checkboxRow}>
                                        <input
                                            id="wc-active"
                                            type="checkbox"
                                            checked={workCenterForm.is_active}
                                            onChange={(e) =>
                                                setWorkCenterForm((prev) => ({
                                                    ...prev,
                                                    is_active: e.target.checked,
                                                }))
                                            }
                                        />
                                        <label htmlFor="wc-active" style={styles.label}>
                                            Active
                                        </label>
                                    </div>
                                </div>
                                <div style={styles.buttonRow}>
                                    <button type="submit" style={styles.button} disabled={saving}>
                                        {saving ? 'Saving…' : editingWorkCenterId ? 'Update' : 'Save work center'}
                                    </button>
                                    {editingWorkCenterId && (
                                        <button
                                            type="button"
                                            style={styles.secondaryButton}
                                            onClick={() => {
                                                setEditingWorkCenterId(null);
                                                setWorkCenterForm(emptyWorkCenter);
                                            }}
                                        >
                                            Cancel
                                        </button>
                                    )}
                                </div>
                            </form>
                        </div>

                        <div style={styles.card}>
                            <h2 style={styles.sectionTitle}>Work centers</h2>
                            <div style={styles.filters}>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Search</label>
                                    <input
                                        style={styles.input}
                                        value={workCenterFilters.search}
                                        placeholder="Code, name…"
                                        onChange={(e) =>
                                            setWorkCenterFilters((prev) => ({
                                                ...prev,
                                                search: e.target.value,
                                            }))
                                        }
                                    />
                                </div>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Site</label>
                                    <select
                                        style={styles.input}
                                        value={workCenterFilters.site}
                                        onChange={(e) =>
                                            setWorkCenterFilters((prev) => ({
                                                ...prev,
                                                site: e.target.value,
                                            }))
                                        }
                                    >
                                        <option value="">All sites</option>
                                        {sites.map((site) => (
                                            <option key={site.id} value={site.id}>
                                                {site.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Active</label>
                                    <select
                                        style={styles.input}
                                        value={workCenterFilters.active}
                                        onChange={(e) =>
                                            setWorkCenterFilters((prev) => ({
                                                ...prev,
                                                active: e.target.value,
                                            }))
                                        }
                                    >
                                        <option value="">All</option>
                                        <option value="yes">Yes</option>
                                        <option value="no">No</option>
                                    </select>
                                </div>
                            </div>
                            <div style={styles.tableWrap}>
                                <table style={styles.table}>
                                    <thead>
                                        <tr>
                                            <th style={styles.th}>Code</th>
                                            <th style={styles.th}>Name</th>
                                            <th style={styles.th}>Site</th>
                                            <th style={styles.th}>Min staffing</th>
                                            <th style={styles.th}>Active</th>
                                            <th style={styles.th}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredWorkCenters.length === 0 ? (
                                            <tr>
                                                <td style={styles.td} colSpan={6}>
                                                    No work centers match the filters.
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredWorkCenters.map((wc) => (
                                            <tr key={wc.id}>
                                                <td style={styles.td}>{wc.code}</td>
                                                <td style={styles.td}>{wc.name}</td>
                                                <td style={styles.td}>{wc.site_name}</td>
                                                <td style={styles.td}>{wc.minimum_staffing}</td>
                                                <td style={styles.td}>{wc.is_active ? 'Yes' : 'No'}</td>
                                                <td style={styles.td}>
                                                    <button
                                                        type="button"
                                                        style={styles.editButton}
                                                        onClick={() => {
                                                            setEditingWorkCenterId(wc.id);
                                                            setWorkCenterForm({
                                                                code: wc.code,
                                                                name: wc.name,
                                                                site: wc.site,
                                                                supervisor: wc.supervisor || '',
                                                                is_active: wc.is_active,
                                                                display_order: wc.display_order,
                                                                minimum_staffing: wc.minimum_staffing,
                                                            });
                                                        }}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        type="button"
                                                        style={styles.dangerButton}
                                                        onClick={() => deleteWorkCenter(wc)}
                                                    >
                                                        Delete
                                                    </button>
                                                </td>
                                            </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}

                {tab === 'gov-work-centers' && (
                    <>
                        <div style={styles.card}>
                            <h2 style={styles.sectionTitle}>
                                {editingGovWorkCenterId ? 'Edit gov work center' : 'Add gov work center'}
                            </h2>
                            <form onSubmit={saveGovWorkCenter}>
                                <div style={styles.formGrid}>
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>Gov Code</label>
                                        <input
                                            style={styles.input}
                                            required
                                            value={govWorkCenterForm.code}
                                            onChange={(e) =>
                                                setGovWorkCenterForm((prev) => ({
                                                    ...prev,
                                                    code: e.target.value,
                                                }))
                                            }
                                        />
                                    </div>
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>Gov Name</label>
                                        <input
                                            style={styles.input}
                                            required
                                            value={govWorkCenterForm.name}
                                            onChange={(e) =>
                                                setGovWorkCenterForm((prev) => ({
                                                    ...prev,
                                                    name: e.target.value,
                                                }))
                                            }
                                        />
                                    </div>
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>Site</label>
                                        <select
                                            style={styles.input}
                                            required
                                            value={govWorkCenterForm.site}
                                            onChange={(e) =>
                                                setGovWorkCenterForm((prev) => ({
                                                    ...prev,
                                                    site: e.target.value,
                                                }))
                                            }
                                        >
                                            <option value="">Select site</option>
                                            {sites.map((site) => (
                                                <option key={site.id} value={site.id}>
                                                    {site.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div style={styles.checkboxRow}>
                                        <input
                                            id="gov-wc-active"
                                            type="checkbox"
                                            checked={govWorkCenterForm.is_active}
                                            onChange={(e) =>
                                                setGovWorkCenterForm((prev) => ({
                                                    ...prev,
                                                    is_active: e.target.checked,
                                                }))
                                            }
                                        />
                                        <label htmlFor="gov-wc-active" style={styles.label}>
                                            Active
                                        </label>
                                    </div>
                                </div>
                                <div style={styles.buttonRow}>
                                    <button type="submit" style={styles.button} disabled={saving}>
                                        {saving
                                            ? 'Saving…'
                                            : editingGovWorkCenterId
                                              ? 'Update'
                                              : 'Save gov work center'}
                                    </button>
                                    {editingGovWorkCenterId && (
                                        <button
                                            type="button"
                                            style={styles.secondaryButton}
                                            onClick={() => {
                                                setEditingGovWorkCenterId(null);
                                                setGovWorkCenterForm(emptyGovWorkCenter);
                                            }}
                                        >
                                            Cancel
                                        </button>
                                    )}
                                </div>
                            </form>
                        </div>

                        <div style={styles.card}>
                            <h2 style={styles.sectionTitle}>Gov work centers</h2>
                            <div style={styles.filters}>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Search</label>
                                    <input
                                        style={styles.input}
                                        value={govWorkCenterFilters.search}
                                        placeholder="Gov code, name…"
                                        onChange={(e) =>
                                            setGovWorkCenterFilters((prev) => ({
                                                ...prev,
                                                search: e.target.value,
                                            }))
                                        }
                                    />
                                </div>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Site</label>
                                    <select
                                        style={styles.input}
                                        value={govWorkCenterFilters.site}
                                        onChange={(e) =>
                                            setGovWorkCenterFilters((prev) => ({
                                                ...prev,
                                                site: e.target.value,
                                            }))
                                        }
                                    >
                                        <option value="">All sites</option>
                                        {sites.map((site) => (
                                            <option key={site.id} value={site.id}>
                                                {site.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Active</label>
                                    <select
                                        style={styles.input}
                                        value={govWorkCenterFilters.active}
                                        onChange={(e) =>
                                            setGovWorkCenterFilters((prev) => ({
                                                ...prev,
                                                active: e.target.value,
                                            }))
                                        }
                                    >
                                        <option value="">All</option>
                                        <option value="yes">Yes</option>
                                        <option value="no">No</option>
                                    </select>
                                </div>
                            </div>
                            <div style={styles.tableWrap}>
                                <table style={styles.table}>
                                    <thead>
                                        <tr>
                                            <th style={styles.th}>Gov Code</th>
                                            <th style={styles.th}>Gov Name</th>
                                            <th style={styles.th}>Site</th>
                                            <th style={styles.th}>Active</th>
                                            <th style={styles.th}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredGovWorkCenters.length === 0 ? (
                                            <tr>
                                                <td style={styles.td} colSpan={5}>
                                                    No gov work centers match the filters.
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredGovWorkCenters.map((gwc) => (
                                            <tr key={gwc.id}>
                                                <td style={styles.td}>{gwc.code}</td>
                                                <td style={styles.td}>{gwc.name}</td>
                                                <td style={styles.td}>{gwc.site_name}</td>
                                                <td style={styles.td}>{gwc.is_active ? 'Yes' : 'No'}</td>
                                                <td style={styles.td}>
                                                    <button
                                                        type="button"
                                                        style={styles.editButton}
                                                        onClick={() => {
                                                            setEditingGovWorkCenterId(gwc.id);
                                                            setGovWorkCenterForm({
                                                                code: gwc.code,
                                                                name: gwc.name,
                                                                site: gwc.site,
                                                                is_active: gwc.is_active,
                                                            });
                                                        }}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        type="button"
                                                        style={styles.dangerButton}
                                                        onClick={() => deleteGovWorkCenter(gwc)}
                                                    >
                                                        Delete
                                                    </button>
                                                </td>
                                            </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}

                {tab === 'shifts' && (
                    <>
                        <div style={styles.card}>
                            <h2 style={styles.sectionTitle}>
                                {editingShiftId ? 'Edit shift' : 'Add shift'}
                            </h2>
                            <form onSubmit={saveShift}>
                                <div style={styles.formGrid}>
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>Code</label>
                                        <input
                                            style={styles.input}
                                            required
                                            value={shiftForm.code}
                                            onChange={(e) =>
                                                setShiftForm((prev) => ({ ...prev, code: e.target.value }))
                                            }
                                        />
                                    </div>
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>Name</label>
                                        <input
                                            style={styles.input}
                                            required
                                            value={shiftForm.name}
                                            onChange={(e) =>
                                                setShiftForm((prev) => ({ ...prev, name: e.target.value }))
                                            }
                                        />
                                    </div>
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>Site</label>
                                        <select
                                            style={styles.input}
                                            required
                                            value={shiftForm.site}
                                            onChange={(e) =>
                                                setShiftForm((prev) => ({ ...prev, site: e.target.value }))
                                            }
                                        >
                                            <option value="">Select site</option>
                                            {sites.map((site) => (
                                                <option key={site.id} value={site.id}>
                                                    {site.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>Start time</label>
                                        <input
                                            style={styles.input}
                                            type="time"
                                            required
                                            value={shiftForm.start_time}
                                            onChange={(e) =>
                                                setShiftForm((prev) => ({
                                                    ...prev,
                                                    start_time: e.target.value,
                                                }))
                                            }
                                        />
                                    </div>
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>End time</label>
                                        <input
                                            style={styles.input}
                                            type="time"
                                            required
                                            value={shiftForm.end_time}
                                            onChange={(e) =>
                                                setShiftForm((prev) => ({
                                                    ...prev,
                                                    end_time: e.target.value,
                                                }))
                                            }
                                        />
                                    </div>
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>Days</label>
                                        <input
                                            style={styles.input}
                                            value={shiftForm.days_of_week}
                                            onChange={(e) =>
                                                setShiftForm((prev) => ({
                                                    ...prev,
                                                    days_of_week: e.target.value,
                                                }))
                                            }
                                        />
                                    </div>
                                    <div style={styles.checkboxRow}>
                                        <input
                                            id="shift-active"
                                            type="checkbox"
                                            checked={shiftForm.is_active}
                                            onChange={(e) =>
                                                setShiftForm((prev) => ({
                                                    ...prev,
                                                    is_active: e.target.checked,
                                                }))
                                            }
                                        />
                                        <label htmlFor="shift-active" style={styles.label}>
                                            Active
                                        </label>
                                    </div>
                                </div>
                                <div style={styles.buttonRow}>
                                    <button type="submit" style={styles.button} disabled={saving}>
                                        {saving ? 'Saving…' : editingShiftId ? 'Update' : 'Save shift'}
                                    </button>
                                    {editingShiftId && (
                                        <button
                                            type="button"
                                            style={styles.secondaryButton}
                                            onClick={() => {
                                                setEditingShiftId(null);
                                                setShiftForm(emptyShift);
                                            }}
                                        >
                                            Cancel
                                        </button>
                                    )}
                                </div>
                            </form>
                        </div>

                        <div style={styles.card}>
                            <h2 style={styles.sectionTitle}>Shifts</h2>
                            <div style={styles.filters}>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Search</label>
                                    <input
                                        style={styles.input}
                                        value={shiftFilters.search}
                                        placeholder="Code, name, days…"
                                        onChange={(e) =>
                                            setShiftFilters((prev) => ({
                                                ...prev,
                                                search: e.target.value,
                                            }))
                                        }
                                    />
                                </div>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Site</label>
                                    <select
                                        style={styles.input}
                                        value={shiftFilters.site}
                                        onChange={(e) =>
                                            setShiftFilters((prev) => ({
                                                ...prev,
                                                site: e.target.value,
                                            }))
                                        }
                                    >
                                        <option value="">All sites</option>
                                        {sites.map((site) => (
                                            <option key={site.id} value={site.id}>
                                                {site.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Active</label>
                                    <select
                                        style={styles.input}
                                        value={shiftFilters.active}
                                        onChange={(e) =>
                                            setShiftFilters((prev) => ({
                                                ...prev,
                                                active: e.target.value,
                                            }))
                                        }
                                    >
                                        <option value="">All</option>
                                        <option value="yes">Yes</option>
                                        <option value="no">No</option>
                                    </select>
                                </div>
                            </div>
                            <div style={styles.tableWrap}>
                                <table style={styles.table}>
                                    <thead>
                                        <tr>
                                            <th style={styles.th}>Code</th>
                                            <th style={styles.th}>Name</th>
                                            <th style={styles.th}>Site</th>
                                            <th style={styles.th}>Hours</th>
                                            <th style={styles.th}>Days</th>
                                            <th style={styles.th}>Active</th>
                                            <th style={styles.th}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredShifts.length === 0 ? (
                                            <tr>
                                                <td style={styles.td} colSpan={7}>
                                                    No shifts match the filters.
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredShifts.map((shift) => (
                                            <tr key={shift.id}>
                                                <td style={styles.td}>{shift.code}</td>
                                                <td style={styles.td}>{shift.name}</td>
                                                <td style={styles.td}>{shift.site_name}</td>
                                                <td style={styles.td}>
                                                    {String(shift.start_time).slice(0, 5)} –{' '}
                                                    {String(shift.end_time).slice(0, 5)}
                                                </td>
                                                <td style={styles.td}>{shift.days_of_week}</td>
                                                <td style={styles.td}>{shift.is_active ? 'Yes' : 'No'}</td>
                                                <td style={styles.td}>
                                                    <button
                                                        type="button"
                                                        style={styles.editButton}
                                                        onClick={() => {
                                                            setEditingShiftId(shift.id);
                                                            setShiftForm({
                                                                code: shift.code,
                                                                name: shift.name,
                                                                site: shift.site,
                                                                start_time: String(shift.start_time).slice(0, 5),
                                                                end_time: String(shift.end_time).slice(0, 5),
                                                                days_of_week: shift.days_of_week,
                                                                is_active: shift.is_active,
                                                            });
                                                        }}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        type="button"
                                                        style={styles.dangerButton}
                                                        onClick={() => deleteShift(shift)}
                                                    >
                                                        Delete
                                                    </button>
                                                </td>
                                            </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}

                {tab === 'positions' && (
                    <>
                        <div style={styles.card}>
                            <h2 style={styles.sectionTitle}>
                                {editingPositionId ? 'Edit position' : 'Add position / billet'}
                            </h2>
                            <form onSubmit={savePosition}>
                                <div style={styles.formGrid}>
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>Internal title</label>
                                        <input
                                            style={styles.input}
                                            required
                                            value={positionForm.title}
                                            onChange={(e) =>
                                                setPositionForm((prev) => ({
                                                    ...prev,
                                                    title: e.target.value,
                                                }))
                                            }
                                        />
                                    </div>
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>Government title</label>
                                        <input
                                            style={styles.input}
                                            placeholder="Title as labeled by the government"
                                            value={positionForm.government_title}
                                            onChange={(e) =>
                                                setPositionForm((prev) => ({
                                                    ...prev,
                                                    government_title: e.target.value,
                                                }))
                                            }
                                        />
                                    </div>
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>Work center</label>
                                        <select
                                            style={styles.input}
                                            required
                                            value={positionForm.work_center}
                                            onChange={(e) =>
                                                setPositionForm((prev) => ({
                                                    ...prev,
                                                    work_center: e.target.value,
                                                }))
                                            }
                                        >
                                            <option value="">Select work center</option>
                                            {workCenters.map((wc) => (
                                                <option key={wc.id} value={wc.id}>
                                                    {wc.site_name}: {wc.code} — {wc.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>Gov Work Center</label>
                                        <select
                                            style={styles.input}
                                            required
                                            value={positionForm.gov_work_center}
                                            onChange={(e) =>
                                                setPositionForm((prev) => ({
                                                    ...prev,
                                                    gov_work_center: e.target.value,
                                                }))
                                            }
                                        >
                                            <option value="">Select gov work center</option>
                                            {govWorkCenters
                                                .filter(
                                                    (gwc) =>
                                                        gwc.is_active ||
                                                        String(gwc.id) ===
                                                            String(positionForm.gov_work_center)
                                                )
                                                .map((gwc) => (
                                                    <option key={gwc.id} value={gwc.id}>
                                                        {gwc.site_name}: {gwc.code} — {gwc.name}
                                                    </option>
                                                ))}
                                        </select>
                                    </div>
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>Required count</label>
                                        <input
                                            style={styles.input}
                                            type="number"
                                            min="0"
                                            value={positionForm.required_count}
                                            onChange={(e) =>
                                                setPositionForm((prev) => ({
                                                    ...prev,
                                                    required_count: e.target.value,
                                                }))
                                            }
                                        />
                                    </div>
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>Skill / rate</label>
                                        <input
                                            style={styles.input}
                                            value={positionForm.skill_rate}
                                            onChange={(e) =>
                                                setPositionForm((prev) => ({
                                                    ...prev,
                                                    skill_rate: e.target.value,
                                                }))
                                            }
                                        />
                                    </div>
                                    <div style={styles.checkboxRow}>
                                        <input
                                            id="pos-funded"
                                            type="checkbox"
                                            checked={positionForm.is_funded}
                                            onChange={(e) =>
                                                setPositionForm((prev) => ({
                                                    ...prev,
                                                    is_funded: e.target.checked,
                                                }))
                                            }
                                        />
                                        <label htmlFor="pos-funded" style={styles.label}>
                                            Funded
                                        </label>
                                    </div>
                                    <div style={styles.checkboxRow}>
                                        <input
                                            id="pos-active"
                                            type="checkbox"
                                            checked={positionForm.is_active}
                                            onChange={(e) =>
                                                setPositionForm((prev) => ({
                                                    ...prev,
                                                    is_active: e.target.checked,
                                                }))
                                            }
                                        />
                                        <label htmlFor="pos-active" style={styles.label}>
                                            Active
                                        </label>
                                    </div>
                                </div>
                                <div style={styles.buttonRow}>
                                    <button type="submit" style={styles.button} disabled={saving}>
                                        {saving ? 'Saving…' : editingPositionId ? 'Update' : 'Save position'}
                                    </button>
                                    {editingPositionId && (
                                        <button
                                            type="button"
                                            style={styles.secondaryButton}
                                            onClick={() => {
                                                setEditingPositionId(null);
                                                setPositionForm(emptyPosition);
                                            }}
                                        >
                                            Cancel
                                        </button>
                                    )}
                                </div>
                            </form>
                        </div>

                        <div style={styles.card}>
                            <h2 style={styles.sectionTitle}>Positions / billets</h2>
                            <div style={styles.filters}>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Search</label>
                                    <input
                                        style={styles.input}
                                        value={positionFilters.search}
                                        placeholder="Title, WC, skill…"
                                        onChange={(e) =>
                                            setPositionFilters((prev) => ({
                                                ...prev,
                                                search: e.target.value,
                                            }))
                                        }
                                    />
                                </div>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Site</label>
                                    <select
                                        style={styles.input}
                                        value={positionFilters.site}
                                        onChange={(e) =>
                                            setPositionFilters((prev) => ({
                                                ...prev,
                                                site: e.target.value,
                                                work_center: '',
                                            }))
                                        }
                                    >
                                        <option value="">All sites</option>
                                        {sites.map((site) => (
                                            <option key={site.id} value={site.id}>
                                                {site.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Work center</label>
                                    <select
                                        style={styles.input}
                                        value={positionFilters.work_center}
                                        onChange={(e) =>
                                            setPositionFilters((prev) => ({
                                                ...prev,
                                                work_center: e.target.value,
                                            }))
                                        }
                                    >
                                        <option value="">All work centers</option>
                                        {workCenters
                                            .filter(
                                                (wc) =>
                                                    !positionFilters.site ||
                                                    String(wc.site) === String(positionFilters.site)
                                            )
                                            .map((wc) => (
                                                <option key={wc.id} value={wc.id}>
                                                    {wc.code} — {wc.name}
                                                </option>
                                            ))}
                                    </select>
                                </div>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Gov Work Center</label>
                                    <select
                                        style={styles.input}
                                        value={positionFilters.gov_work_center}
                                        onChange={(e) =>
                                            setPositionFilters((prev) => ({
                                                ...prev,
                                                gov_work_center: e.target.value,
                                            }))
                                        }
                                    >
                                        <option value="">All gov work centers</option>
                                        {govWorkCenters
                                            .filter(
                                                (gwc) =>
                                                    !positionFilters.site ||
                                                    String(gwc.site) === String(positionFilters.site)
                                            )
                                            .map((gwc) => (
                                                <option key={gwc.id} value={gwc.id}>
                                                    {gwc.code} — {gwc.name}
                                                </option>
                                            ))}
                                    </select>
                                </div>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Active</label>
                                    <select
                                        style={styles.input}
                                        value={positionFilters.active}
                                        onChange={(e) =>
                                            setPositionFilters((prev) => ({
                                                ...prev,
                                                active: e.target.value,
                                            }))
                                        }
                                    >
                                        <option value="">All</option>
                                        <option value="yes">Yes</option>
                                        <option value="no">No</option>
                                    </select>
                                </div>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Funded</label>
                                    <select
                                        style={styles.input}
                                        value={positionFilters.funded}
                                        onChange={(e) =>
                                            setPositionFilters((prev) => ({
                                                ...prev,
                                                funded: e.target.value,
                                            }))
                                        }
                                    >
                                        <option value="">All</option>
                                        <option value="yes">Yes</option>
                                        <option value="no">No</option>
                                    </select>
                                </div>
                            </div>
                            <div style={styles.tableWrap}>
                                <table style={styles.table}>
                                    <thead>
                                        <tr>
                                            {[
                                                { key: 'title', label: 'Internal title' },
                                                { key: 'government_title', label: 'Government title' },
                                                { key: 'work_center', label: 'Work center' },
                                                { key: 'gov_work_center', label: 'Gov Work Center' },
                                                { key: 'site', label: 'Site' },
                                                { key: 'required', label: 'Required' },
                                                { key: 'skill_rate', label: 'Skill/rate' },
                                                { key: 'funded', label: 'Funded' },
                                                { key: 'active', label: 'Active' },
                                            ].map((col) => (
                                                <th
                                                    key={col.key}
                                                    style={{ ...styles.th, cursor: 'pointer', userSelect: 'none' }}
                                                    onClick={() => togglePositionSort(col.key)}
                                                    title={`Sort by ${col.label}`}
                                                >
                                                    {col.label}
                                                    {positionSort.key === col.key
                                                        ? (positionSort.dir === 'asc' ? ' ▲' : ' ▼')
                                                        : ''}
                                                </th>
                                            ))}
                                            <th style={styles.th}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedPositions.length === 0 ? (
                                            <tr>
                                                <td style={styles.td} colSpan={10}>
                                                    No positions match the filters.
                                                </td>
                                            </tr>
                                        ) : (
                                            sortedPositions.map((pos) => (
                                            <tr key={pos.id}>
                                                <td style={styles.td}>{pos.title}</td>
                                                <td style={styles.td}>{pos.government_title || '—'}</td>
                                                <td style={styles.td}>
                                                    {pos.work_center_code} — {pos.work_center_name}
                                                </td>
                                                <td style={styles.td}>
                                                    {pos.gov_work_center_code
                                                        ? `${pos.gov_work_center_code} — ${pos.gov_work_center_name}`
                                                        : '—'}
                                                </td>
                                                <td style={styles.td}>{pos.site_name}</td>
                                                <td style={styles.td}>{pos.required_count}</td>
                                                <td style={styles.td}>{pos.skill_rate || '—'}</td>
                                                <td style={styles.td}>{pos.is_funded ? 'Yes' : 'No'}</td>
                                                <td style={styles.td}>{pos.is_active ? 'Yes' : 'No'}</td>
                                                <td style={styles.td}>
                                                    <button
                                                        type="button"
                                                        style={styles.editButton}
                                                        onClick={() => {
                                                            setEditingPositionId(pos.id);
                                                            setPositionForm({
                                                                title: pos.title,
                                                                government_title: pos.government_title || '',
                                                                work_center: pos.work_center,
                                                                gov_work_center: pos.gov_work_center || '',
                                                                required_count: pos.required_count,
                                                                skill_rate: pos.skill_rate || '',
                                                                is_funded: pos.is_funded,
                                                                is_active: pos.is_active,
                                                            });
                                                        }}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        type="button"
                                                        style={styles.dangerButton}
                                                        onClick={() => deletePosition(pos)}
                                                    >
                                                        Delete
                                                    </button>
                                                </td>
                                            </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Separation Modal Popup */}
            {separatingEmployee && (
                <div style={styles.modalOverlay}>
                    <div style={styles.modalContent}>
                        <h3 style={{ marginTop: 0, marginBottom: '15px' }}>Separate Employee</h3>
                        <p style={{ fontSize: '14px', marginBottom: '20px' }}>
                            Mark <strong>{separatingEmployee.display_name || separatingEmployee.employee_number}</strong> as Separated? Soft-delete keeps history.
                        </p>
                        <form onSubmit={confirmSeparation}>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Separation Date</label>
                                <input
                                    type="date"
                                    required
                                    style={styles.input}
                                    value={separationDate}
                                    onChange={(e) => setSeparationDate(e.target.value)}
                                />
                            </div>
                            <div style={styles.modalActions}>
                                <button
                                    type="button"
                                    style={styles.secondaryButton}
                                    onClick={() => setSeparatingEmployee(null)}
                                >
                                    Cancel
                                </button>
                                <button type="submit" style={{ ...styles.dangerButton, padding: '10px 14px', fontSize: '14px' }}>
                                    Confirm Separation
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

        </div>
    );
}

export default ManningPage;